import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Download, FileText, IndianRupee, Lock, Minus, Play, Send, Settings, Unlock, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getPayrollPeriods,
  computePayroll,
  getPayslips,
  setPayrollPeriodState,
  downloadPayrollFile,
  downloadPayslip,
  submitPayrollForApproval
} from '../../services/api/implementation/impl';
import {
  Callout,
  StatusPill,
  Tile
} from '../../../shared/components/ui/primitives';
import { useApproverAccess } from '../../hooks/useApproverAccess';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_STYLE = {
  DRAFT: 'bg-gray-100 text-gray-600',
  COMPUTED: 'bg-warning-bg text-warning-fg',
  LOCKED: 'bg-info-bg text-info-fg',
  PAID: 'bg-success-bg text-success-fg'
};

/**
 * Running a month of payroll.
 *
 * The sequence is compute → review → lock → pay, and the screen enforces it in
 * that order. Locking is what freezes attendance for the month, so it should
 * only happen once somebody has actually looked at the numbers — which is why
 * the warnings from a compute are shown prominently rather than as a toast that
 * disappears.
 */
const PayrollRun = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const queryClient = useQueryClient();
  const now = new Date();
  // Defaults to last month: payroll is run for a month that has finished.
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [year, setYear] = useState(
    now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  );
  const [selected, setSelected] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [reopening, setReopening] = useState(null);

  // Locking is the approval: only the account owner or the assigned payroll
  // approver does it. Everyone else sends the computed period for approval.
  // Until /approvals/me answers, neither control is drawn; if it fails, the
  // old Lock stays and the server has the last word.
  const access = useApproverAccess();
  const accessKnown = access.loaded || access.failed;
  const payrollApprover = access.loaded ? access.canApprove('PAYROLL') : true;

  const periods = useQuery({
    queryKey: ['payrollPeriods'],
    queryFn: getPayrollPeriods
  });

  // Keep the open period in step with the list after a refetch, so a period
  // approved or sent back elsewhere does not keep showing its old state here.
  useEffect(() => {
    if (!selected?.id || !Array.isArray(periods.data)) return;
    const fresh = periods.data.find((p) => p.id === selected.id);
    if (fresh && fresh !== selected) setSelected((prev) => ({ ...prev, ...fresh }));
    // Deliberately keyed on periods.data alone. The merge above builds a new
    // object every time, so `fresh !== selected` never settles and adding
    // `selected` here would re-trigger the effect on its own write, forever.
  }, [periods.data]);

  const payslips = useQuery({
    queryKey: ['payslips', selected?.id],
    queryFn: () => getPayslips(selected.id),
    enabled: Boolean(selected?.id)
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['payrollPeriods'] });
    queryClient.invalidateQueries({ queryKey: ['payslips'] });
  };

  const compute = useMutation({
    mutationFn: () => computePayroll(month, year),
    onSuccess: (data) => {
      refresh();
      setSelected(data.period);
      setWarnings(data.warnings ?? []);
      toast.success(`Computed for ${data.period.employee_count} employee(s)`);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not compute payroll')
  });

  const changeState = useMutation({
    mutationFn: ({ id, action, reason }) =>
      setPayrollPeriodState(id, action, reason),
    onSuccess: (data) => {
      refresh();
      setSelected(data);
      setReopening(null);
      toast.success('Period updated');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not update the period')
  });

  const submitForApproval = useMutation({
    mutationFn: (id) => submitPayrollForApproval(id),
    onSuccess: (data) => {
      refresh();
      if (data) setSelected(data);
      toast.success('Sent for approval');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not send for approval')
  });

  const download = useMutation({
    mutationFn: ({ kind, filename }) =>
      downloadPayrollFile(selected.id, kind, filename),
    onSuccess: (result) => {
      if (result?.skipped) {
        // Never a toast that vanishes — somebody not in the bank file is
        // somebody who does not get paid.
        setWarnings((prev) => [
          ...prev,
          `Left out of the bank file — ${result.skipped}`
        ]);
        toast.error('Some employees were left out of the bank file — see above');
      } else {
        toast.success('Downloaded');
      }
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not download')
  });

  const periodRows = periods.data ?? [];
  const slips = payslips.data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Payroll' }} />

      <div className="p-4 space-y-4">
        <div className="flex justify-end">
          <Link
            to="/builder/hr/payroll/setup"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <Settings size={14} /> Payroll setup
          </Link>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                {MONTHS.map((name, index) => (
                  <option key={name} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Year
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-28"
              />
            </div>
            <button
              type="button"
              disabled={compute.isPending}
              onClick={() => {
                setWarnings([]);
                compute.mutate();
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
            >
              <Play size={15} />
              {compute.isPending ? 'Computing…' : 'Compute payroll'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Safe to run repeatedly while the period is open — recomputing
            replaces the figures rather than adding to them.
          </p>
        </div>

        {warnings.length > 0 && (
          <div className="rounded-control border border-warning-border bg-warning-bg px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium text-warning-fg">
              <AlertTriangle size={16} />
              {warnings.length} thing{warnings.length === 1 ? '' : 's'} to look at
              before locking
            </div>
            <ul className="mt-2 text-xs text-warning-fg space-y-1 list-disc pl-5">
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 font-medium text-gray-800">
              Periods
            </div>
            {periods.isFetching ? (
              <Loader />
            ) : periodRows.length === 0 ? (
              <div className="py-10 text-center text-gray-500 text-sm px-4">
                No payroll has been run yet.
              </div>
            ) : (
              <div className="max-h-[520px] overflow-y-auto">
                {periodRows.map((period) => (
                  <button
                    key={period.id}
                    type="button"
                    onClick={() => {
                      setSelected(period);
                      setWarnings([]);
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                      selected?.id === period.id ? 'bg-brand-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {MONTHS[period.month - 1]} {period.year}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          STATUS_STYLE[period.status] ?? 'bg-gray-100'
                        }`}
                      >
                        {period.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {period.employee_count} employee(s) · net{' '}
                      {money(period.total_net)}
                      {period.status === 'COMPUTED' && period.approval_requested_at
                        ? ' · awaiting approval'
                        : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            {!selected ? (
              <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
                <FileText size={22} className="mx-auto mb-2 text-gray-400" />
                Select a period, or compute one above.
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {MONTHS[selected.month - 1]} {selected.year}
                      </div>
                      <div className="text-xs text-gray-500">
                        {selected.total_working_days} days ·{' '}
                        {selected.employee_count} employee(s)
                      </div>
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded text-xs font-medium ${
                        STATUS_STYLE[selected.status] ?? 'bg-gray-100'
                      }`}
                    >
                      {selected.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <Tile variant="card" label="Gross" value={money(selected.total_gross)}
            icon={<IndianRupee size={18} />}
          />
                    <Tile variant="card" label="Deductions" value={money(selected.total_deductions)}
            icon={<Minus size={18} />}
          />
                    <Tile variant="card"
                      label="Net payable"
                      value={money(selected.total_net)}
                      tone="brand"
            icon={<Wallet size={18} />}
          />
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() =>
                        download.mutate({
                          kind: 'register',
                          filename: `Salary-Register-${selected.month}-${selected.year}.csv`
                        })
                      }
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm"
                    >
                      <Download size={14} /> Salary register
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        download.mutate({
                          kind: 'bank-file',
                          filename: `Bank-Transfer-${selected.month}-${selected.year}.csv`
                        })
                      }
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm"
                    >
                      <IndianRupee size={14} /> Bank file
                    </button>

                    {selected.status === 'COMPUTED' && accessKnown && payrollApprover && (
                      <button
                        type="button"
                        disabled={changeState.isPending}
                        onClick={() =>
                          changeState.mutate({ id: selected.id, action: 'lock' })
                        }
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-info text-white text-sm disabled:opacity-50"
                      >
                        <Lock size={14} /> Approve &amp; lock
                      </button>
                    )}

                    {selected.status === 'COMPUTED' &&
                      accessKnown &&
                      !payrollApprover &&
                      (selected.approval_requested_at ? (
                        <StatusPill tone="warning" className="self-center">
                          <Clock size={13} className="mr-1" />
                          Waiting for approval since {shortDate(selected.approval_requested_at)}
                        </StatusPill>
                      ) : (
                        <button
                          type="button"
                          disabled={submitForApproval.isPending}
                          onClick={() => submitForApproval.mutate(selected.id)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-700 text-white text-sm disabled:opacity-50"
                        >
                          <Send size={14} />
                          {submitForApproval.isPending ? 'Sending…' : 'Send for approval'}
                        </button>
                      ))}

                    {selected.status === 'LOCKED' && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            changeState.mutate({ id: selected.id, action: 'paid' })
                          }
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success text-white text-sm"
                        >
                          <CheckCircle2 size={14} /> Mark as paid
                        </button>
                        {accessKnown && payrollApprover && (
                          <button
                            type="button"
                            onClick={() => setReopening(selected)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-warning-border text-warning-fg text-sm"
                          >
                            <Unlock size={14} /> Reopen
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {selected.status === 'COMPUTED' && selected.approval_return_note && (
                    <Callout tone="warning" icon={<AlertTriangle size={14} />} className="mt-3">
                      Sent back: {selected.approval_return_note}
                    </Callout>
                  )}

                  {selected.status === 'COMPUTED' &&
                    payrollApprover &&
                    selected.approval_requested_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Sent for approval on {shortDate(selected.approval_requested_at)}.
                      </p>
                    )}

                  {selected.status === 'LOCKED' && (
                    <p className="text-xs text-gray-500 mt-2">
                      Attendance and leave for this month are frozen while it is
                      locked.
                    </p>
                  )}

                  {(selected.reopen_log ?? []).length > 0 && (
                    <div className="mt-3 text-xs text-warning-fg">
                      Reopened {selected.reopen_log.length} time(s):{' '}
                      {selected.reopen_log[selected.reopen_log.length - 1].reason}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 font-medium text-gray-800">
                    Payslips
                  </div>
                  {payslips.isFetching ? (
                    <Loader />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <Th>Employee</Th>
                            <Th>Payable</Th>
                            <Th>LOP</Th>
                            <Th>Gross</Th>
                            <Th>Deductions</Th>
                            <Th>Net</Th>
                            <Th />
                          </tr>
                        </thead>
                        <tbody>
                          {slips.length === 0 && (
                            <tr>
                              <td colSpan={7} className="text-center py-10 text-gray-500">
                                No payslips in this period.
                              </td>
                            </tr>
                          )}
                          {slips.map((slip) => (
                            <tr key={slip.id} className="border-t border-gray-100">
                              <Td>
                                <div className="font-medium text-gray-900">
                                  {slip.user?.name ?? `#${slip.user_id}`}
                                </div>
                              </Td>
                              <Td>{num(slip.payable_days)}</Td>
                              <Td className={Number(slip.lop_days) > 0 ? 'text-warning-fg' : ''}>
                                {num(slip.lop_days)}
                              </Td>
                              <Td>{money(slip.gross)}</Td>
                              <Td>{money(slip.total_deductions)}</Td>
                              <Td className="font-medium">{money(slip.net)}</Td>
                              <Td>
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadPayslip(
                                      slip.id,
                                      `Payslip-${slip.user?.name ?? slip.user_id}.pdf`
                                    ).catch(() => toast.error('Could not download'))
                                  }
                                  className="text-brand-700 text-xs underline"
                                >
                                  Payslip
                                </button>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {reopening && (
        <ReopenModal
          period={reopening}
          busy={changeState.isPending}
          onClose={() => setReopening(null)}
          onConfirm={(reason) =>
            changeState.mutate({ id: reopening.id, action: 'reopen', reason })
          }
        />
      )}
    </Layout>
  );
};

const ReopenModal = ({ period, busy, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');

  return (
    <Modal name="Reopen this period" onClose={onClose} width="440px">
      <div className="space-y-3">
        <div className="rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
          People may already have been paid against{' '}
          {MONTHS[period.month - 1]} {period.year}. Reopening lets attendance and
          payslips change again, and every reopen is recorded.
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reason
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being reopened?"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !reason.trim()}
            onClick={() => onConfirm(reason.trim())}
            className="px-4 py-2 rounded bg-warning text-white text-sm disabled:opacity-50"
          >
            {busy ? 'Reopening…' : 'Reopen period'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function num(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

export default PayrollRun;
